/*
  Effects based on React Bits (https://reactbits.dev): BlurText, ClickSpark, CountUp
  (LogoLoop lives in sections/ct-logo-loop.liquid, ShinyText in assets/ct-effects.css).

  MIT + Commons Clause License Condition v1.0
  Copyright (c) 2026 David Haz

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, and distribute the Software as part of an
  application, website, or product, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in all
  copies or substantial portions of the Software.

  Commons Clause Restriction: You may use this Software, including for any commercial
  purpose, so long as you do not sell, sublicense, or redistribute the components
  themselves-whether alone, in a bundle, or as a ported version.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
  SOFTWARE.
*/
(() => {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const SPARK_COLOR = '#ff568b';
  const below = (el) => el.getBoundingClientRect().top > window.innerHeight;
  const onVisible = (els, run) => {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          run(entry.target);
        }),
      { rootMargin: '0px 0px -10% 0px' }
    );
    els.forEach((el) => io.observe(el));
  };

  // BlurText: section headings reveal word by word. Headings already on screen at load stay as they are (no flash).
  const headings = [...document.querySelectorAll('main h2:not(.visually-hidden)')].filter(
    (h) => h.children.length === 0 && h.textContent.trim() && !h.closest('.cts') && below(h)
  );
  headings.forEach((h) => {
    const words = h.textContent.trim().split(/\s+/);
    h.textContent = '';
    words.forEach((word, i) => {
      const span = document.createElement('span');
      span.className = 'ct-blur-word';
      span.style.transitionDelay = `${i * 120}ms`;
      span.textContent = word;
      h.append(span, i < words.length - 1 ? ' ' : '');
    });
    h.classList.add('ct-blur-pending');
  });
  onVisible(headings, (h) => h.classList.remove('ct-blur-pending'));

  // CountUp: [data-ct-count] elements and the Sortierservice package prices count up to the number already in their text (German format, e.g. "0,04 €", "2.500").
  const counters = [...document.querySelectorAll('[data-ct-count], .cts .pkg-price .val')].filter(below);
  counters.forEach((el) => {
    const text = el.textContent;
    const match = text.match(/\d{1,3}(?:\.\d{3})*(?:,\d+)?|\d+(?:,\d+)?/);
    if (!match) return;
    const decimals = (match[0].split(',')[1] || '').length;
    const target = parseFloat(match[0].replace(/\./g, '').replace(',', '.'));
    const format = (n) => n.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    el.ctCount = { before: text.slice(0, match.index), after: text.slice(match.index + match[0].length), target, format };
    el.textContent = el.ctCount.before + format(0) + el.ctCount.after;
  });
  onVisible(
    counters.filter((el) => el.ctCount),
    (el) => {
      const { before, after, target, format } = el.ctCount;
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / 1400);
        el.textContent = before + format(target * (1 - Math.pow(1 - p, 3))) + after;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  );

  // ClickSpark: a burst of sparks where an add-to-cart button is clicked.
  let canvas = null;
  let sparks = [];
  const draw = (now) => {
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    sparks = sparks.filter((s) => now - s.start < 450);
    sparks.forEach((s) => {
      const p = (now - s.start) / 450;
      const eased = p * (2 - p);
      const distance = eased * 40;
      const length = 12 * (1 - eased);
      ctx.strokeStyle = SPARK_COLOR;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(s.x + distance * Math.cos(s.angle), s.y + distance * Math.sin(s.angle));
      ctx.lineTo(s.x + (distance + length) * Math.cos(s.angle), s.y + (distance + length) * Math.sin(s.angle));
      ctx.stroke();
    });
    if (sparks.length) requestAnimationFrame(draw);
  };

  document.addEventListener('click', (e) => {
    const button = e.target instanceof Element ? e.target.closest('button[name="add"], .quick-add__submit') : null;
    if (!button || button.disabled) return;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.setAttribute('aria-hidden', 'true');
      canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483647';
      document.body.append(canvas);
    }
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const rect = button.getBoundingClientRect();
    const keyboard = e.detail === 0; // Enter/Space have no pointer position
    const x = keyboard ? rect.left + rect.width / 2 : e.clientX;
    const y = keyboard ? rect.top + rect.height / 2 : e.clientY;
    const start = performance.now();
    const idle = sparks.length === 0;
    for (let i = 0; i < 10; i++) sparks.push({ x, y, angle: (2 * Math.PI * i) / 10, start });
    if (idle) requestAnimationFrame(draw);
  });
})();
